import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';

import { useReportDenominationOptions, useReportFilterOptions } from '../api/reports.queries';
import {
  CLEAR_REPORT_FILTERS,
  hasReportFilters,
  type ReportFilterChanges,
} from '../lib/report-filters';
import type { ReportFilterOption, ReportFilters } from '../types/report.types';

/** The "no filter" choice at the top of each list. */
const ALL = '__all__';

const EMPTY_OPTIONS: ReportFilterOption[] = [];

const withAll = (label: string, options: SearchableOption[]): SearchableOption[] => [
  { value: ALL, label },
  ...options,
];

const labelOf = (options: ReportFilterOption[], value: string) =>
  options.find((option) => option.value === value)?.label ?? null;

interface ActiveFilter {
  key: string;
  name: string;
  value: string;
  clear: ReportFilterChanges;
}

/**
 * The report filters — payment channel, provider, brand and denomination —
 * shared by every tab. The choices come from the backend for the staff
 * member's countries; the denomination list is a brand's, so it waits for a
 * brand. Each change is reported as one patch of URL keys.
 */
export function ReportsFilters({
  filters,
  countryId,
  onChange,
}: {
  filters: ReportFilters;
  /** Undefined = all the staff member's countries. */
  countryId: number | undefined;
  onChange: (changes: ReportFilterChanges) => void;
}) {
  const options = useReportFilterOptions(countryId);
  const denominations = useReportDenominationOptions(filters.brandId, countryId);
  const channels = options.data?.channels ?? EMPTY_OPTIONS;
  const providers = options.data?.providers ?? EMPTY_OPTIONS;
  const brands = options.data?.brands ?? EMPTY_OPTIONS;
  const denominationOptions = denominations.data ?? EMPTY_OPTIONS;

  const providerValue = filters.providerId?.toString();
  const brandValue = filters.brandId?.toString();
  const denominationValue = filters.denominationId?.toString();

  const active: ActiveFilter[] = [];
  if (filters.channel !== undefined)
    active.push({
      key: 'channel',
      name: 'Payment channel',
      value: labelOf(channels, filters.channel) ?? filters.channel,
      clear: { channel: null },
    });
  if (providerValue !== undefined)
    active.push({
      key: 'provider',
      name: 'Provider',
      value: labelOf(providers, providerValue) ?? `#${providerValue}`,
      clear: { providerId: null },
    });
  if (brandValue !== undefined)
    active.push({
      key: 'brand',
      name: 'Brand',
      value: labelOf(brands, brandValue) ?? `#${brandValue}`,
      // The denomination is the brand's: it goes with it.
      clear: { brandId: null, denominationId: null },
    });
  if (denominationValue !== undefined)
    active.push({
      key: 'denomination',
      name: 'Denomination',
      value: labelOf(denominationOptions, denominationValue) ?? `#${denominationValue}`,
      clear: { denominationId: null },
    });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2" data-print="hide">
        <SearchableSelect
          id="report-filter-channel"
          label="Payment channel"
          value={filters.channel ?? ALL}
          selectedLabel={filters.channel ?? null}
          options={withAll(
            'All payment channels',
            channels.map((option) => ({ ...option, description: option.value })),
          )}
          onChange={(value) => onChange({ channel: value === ALL ? null : value })}
          placeholder="All payment channels"
          searchPlaceholder="Search channels…"
          loading={options.isLoading}
          triggerClassName="w-52"
        />
        <SearchableSelect
          id="report-filter-provider"
          label="Provider"
          value={providerValue ?? ALL}
          selectedLabel={providerValue ? `#${providerValue}` : null}
          options={withAll('All providers', providers)}
          onChange={(value) => onChange({ providerId: value === ALL ? null : value })}
          placeholder="All providers"
          searchPlaceholder="Search providers…"
          loading={options.isLoading}
          triggerClassName="w-44"
        />
        <SearchableSelect
          id="report-filter-brand"
          label="Brand"
          value={brandValue ?? ALL}
          selectedLabel={brandValue ? `#${brandValue}` : null}
          options={withAll('All brands', brands)}
          // A new brand (or none) always clears the denomination.
          onChange={(value) =>
            onChange({ brandId: value === ALL ? null : value, denominationId: null })
          }
          placeholder="All brands"
          searchPlaceholder="Search brands…"
          loading={options.isLoading}
          triggerClassName="w-48"
        />
        <SearchableSelect
          id="report-filter-denomination"
          label="Denomination"
          value={denominationValue ?? ALL}
          selectedLabel={denominationValue ? `#${denominationValue}` : null}
          options={withAll('All denominations', denominationOptions)}
          onChange={(value) => onChange({ denominationId: value === ALL ? null : value })}
          placeholder={brandValue ? 'All denominations' : 'Choose a brand first'}
          searchPlaceholder="Search denominations…"
          emptyText="No denominations of this brand were sold here."
          loading={denominations.isLoading}
          disabled={brandValue === undefined}
          triggerClassName="w-48"
        />
        {(options.error || denominations.error) && (
          <span className="text-muted-foreground text-xs" role="status">
            Some filter choices could not be loaded.{' '}
            <button
              type="button"
              className="text-primary rounded-sm underline-offset-2 hover:underline"
              onClick={() => {
                if (options.error) void options.refetch();
                if (denominations.error) void denominations.refetch();
              }}
            >
              Retry
            </button>
          </span>
        )}
      </div>

      {hasReportFilters(filters) && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-muted-foreground text-xs">Filtered by</span>
          <ul aria-label="Active filters" className="flex flex-wrap items-center gap-1.5">
            {active.map((chip) => (
              <li
                key={chip.key}
                className="bg-accent text-accent-foreground inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs"
              >
                <span className="text-muted-foreground">{chip.name}:</span>
                <span className="font-medium">{chip.value}</span>
                <button
                  type="button"
                  aria-label={`Remove ${chip.name.toLowerCase()} filter`}
                  className="text-muted-foreground hover:text-foreground rounded-sm"
                  onClick={() => onChange(chip.clear)}
                  data-print="hide"
                >
                  <X className="size-3" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          <span data-print="hide">
            <Button
              variant="ghost"
              size="xs"
              className="text-xs"
              onClick={() => onChange(CLEAR_REPORT_FILTERS)}
            >
              Clear filters
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
